import { useEffect, useRef, useState } from "react";
import { PencilSquareIcon, XMarkIcon } from "@heroicons/react/24/outline";
import Button from "../common/Button";

// `preview` is the item rendered in its timeline style (no hover menu), so
// the detective annotates against the whole entry, not a clipped label.
export type TimelineNoteTarget = { key: string; label: string; note: string; preview?: React.ReactNode };

interface Props {
  target: TimelineNoteTarget | null; // null = closed
  onClose: () => void;
  onSave: (note: string) => Promise<void>;
}

// Detective's note on a timeline item. Same shell as LeadNoteModal. The
// note sits beside the AI's text (never replaces it) and prints under the
// entry in the exported chronology.
const TimelineNoteModal: React.FC<Props> = ({ target, onClose, onSave }) => {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  // The draft resets only when a different item opens, keyed on the entry
  // key. Parents pass `onClose` inline and re-render on the detail poll, so
  // depending on the callback here would wipe the draft mid-typing; the
  // Escape handler reads the latest callback through a ref instead.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const targetKey = target?.key ?? null;

  useEffect(() => {
    if (!targetKey) return;
    setNote(target?.note || "");
    setSaving(false);
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onCloseRef.current();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetKey]);

  if (!target) return null;

  const save = async () => {
    setSaving(true);
    try {
      await onSave(note.trim());
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <dialog open className="modal modal-open">
      <div className="modal-box max-w-2xl w-11/12 p-0 overflow-hidden rounded-2xl">
        <div className="flex items-start gap-3 px-6 pt-6 pb-4">
          <div className="size-10 shrink-0 rounded-xl bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 flex items-center justify-center">
            <PencilSquareIcon className="size-6" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-bold text-base-content leading-tight">{target.note ? "Edit note" : "Add a note"}</h3>
            <p className="text-sm text-base-content/60 mt-0.5">Your note sits beside this entry on the timeline and prints under it when exported.</p>
          </div>
          <button type="button" onClick={onClose} disabled={saving} className="p-1 rounded-md text-base-content/50 hover:bg-base-200 hover:text-base-content" title="Close">
            <XMarkIcon className="size-5" />
          </button>
        </div>
        <div className="px-6">
          {target.preview ? (
            <div className="mb-4 max-h-72 overflow-y-auto rounded-lg">{target.preview}</div>
          ) : (
            <p className="mb-4 text-sm text-base-content/80">{target.label}</p>
          )}
          <textarea
            className="textarea textarea-bordered bg-base-100 w-full text-sm focus:outline-none focus:border-blue-500"
            rows={4}
            autoFocus
            maxLength={4000}
            placeholder="What you know about this that the AI doesn't, or how it should be read."
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") save();
            }}
          />
          <p className="mt-1.5 text-[11px] text-base-content/50">Shown beside the entry with your name, and printed under it in the exported chronology. The AI's text is never changed.</p>
        </div>
        <div className="px-6 py-5 flex items-center justify-end gap-2">
          {target.note && (
            <Button type="button" variant="secondary" size="sm" className="mr-auto hover:text-red-600 dark:hover:text-red-400" onClick={() => { setNote(""); onSave("").then(onClose); }} disabled={saving}>
              Remove note
            </Button>
          )}
          <Button type="button" variant="secondary" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="button" variant="primary" size="sm" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save note"}
          </Button>
        </div>
      </div>
      <button type="button" className="modal-backdrop" onClick={() => !saving && onClose()} aria-label="Close" />
    </dialog>
  );
};

export default TimelineNoteModal;
